/**
 * Reveal an element below a sticky/fixed element once the software keyboard
 * has gone — the scroll restore used when a mobile focus mode closes (the
 * product-page MobileFocusModal and the DS Input focus sheet).
 *
 * Why not one measurement after a fixed delay: on close the visual viewport is
 * still animating (keyboard sliding away, iOS undoing its own keyboard pan,
 * Chrome reflowing the layout viewport), the sticky element's bottom edge
 * changes once the page header scrolls out from above it, and a 3D gallery
 * flips from position:fixed back to sticky on the next React commit. Numbers
 * taken mid-flight land the field under the sticky element "sometimes".
 *
 * So: wait for the viewport to go quiet, then converge — measure, scroll to
 * the ABSOLUTE target, re-measure over a few animation frames until the target
 * sits where it should for two consecutive frames. Instant scrolling only
 * (native feel, and smooth scrolling would fight the loop); absolute targets
 * because iOS applies scrolls asynchronously and relative steps compound (see
 * the note in the loop). Everything is cancellable.
 */

export const REVEAL_GAP = 20;

/**
 * Diagnostics only (`?reveal=debug`): trace what the browser does while a
 * focus mode OPENS — every visual-viewport event (height @ offset, scrollY),
 * and animation frames that took longer than `jankMs` (main-thread stalls
 * that would make a camera tween stutter). Runs for `durationMs`.
 */
export function traceFocusOpen(label, { durationMs = 1500, jankMs = 40 } = {}) {
  if (!debugOn()) return () => {};
  const t0 = performance.now();
  const stamp = () => `+${Math.round(performance.now() - t0)}ms`;
  const vv = window.visualViewport;
  const onVv = (e) => debugLine(`${stamp()} vv.${e.type} ${Math.round(vv.height)}@${Math.round(vv.offsetTop)} y=${Math.round(window.scrollY)}`);
  vv?.addEventListener('resize', onVv);
  vv?.addEventListener('scroll', onVv);
  let last = t0; let raf = 0; let worst = 0;
  const tick = () => {
    const now = performance.now();
    const gap = now - last;
    if (gap > jankMs) debugLine(`${stamp()} jank: frame took ${Math.round(gap)}ms`);
    worst = Math.max(worst, gap);
    last = now;
    if (now - t0 < durationMs) raf = requestAnimationFrame(tick);
    else debugLine(`${stamp()} open trace end (worst frame ${Math.round(worst)}ms)`);
  };
  debugLine(`── open(${label}) y=${Math.round(window.scrollY)} vv=${vv ? `${Math.round(vv.height)}@${Math.round(vv.offsetTop)}` : '-'} root=${(document.querySelector('.pp') || {}).className || '-'}`);
  raf = requestAnimationFrame(tick);
  const stop = () => { vv?.removeEventListener('resize', onVv); vv?.removeEventListener('scroll', onVv); cancelAnimationFrame(raf); };
  setTimeout(stop, durationMs + 100);
  return stop;
}

/**
 * Run `callback` once the visual viewport stops changing.
 * - fires after `initialDelay` if no viewport event arrives at all;
 * - every `resize`/`scroll` event on the visual viewport re-arms a
 *   `quietDelay` timer, so the callback runs `quietDelay` after the LAST event;
 * - `maxWait` caps the wait when events keep coming (e.g. a long address-bar
 *   animation), so the callback is never starved.
 * Returns a cancel function.
 */
export function onViewportSettled(callback, options = {}) {
  const {
    viewport = typeof window !== 'undefined' ? window.visualViewport : null,
    initialDelay = 300,
    quietDelay = 150,
    maxWait = 1000,
    events = ['resize', 'scroll'],
  } = options;
  let done = false;
  let quietTimer = null;
  let capTimer = null;

  const cleanup = () => {
    clearTimeout(quietTimer);
    clearTimeout(capTimer);
    if (viewport) events.forEach((ev) => viewport.removeEventListener(ev, onEvent));
  };
  const finish = () => {
    if (done) return;
    done = true;
    cleanup();
    callback();
  };
  function onEvent() {
    if (done) return;
    clearTimeout(quietTimer);
    quietTimer = setTimeout(finish, quietDelay);
  }

  quietTimer = setTimeout(finish, initialDelay);
  if (viewport) {
    events.forEach((ev) => viewport.addEventListener(ev, onEvent));
    capTimer = setTimeout(finish, maxWait);
  }
  return () => {
    done = true;
    cleanup();
  };
}

const resolveEl = (spec) => {
  const el = typeof spec === 'function' ? spec() : spec;
  return el && el.isConnected ? el : null;
};

/* ── Diagnostics (opt-in) ─────────────────────────────────────────────────
 * Open the page with `?reveal=debug` (or set window.__revealDebug = true) to
 * get an on-screen trace of every reveal: per-frame measurements, then
 * re-checks 250/750/1500ms after the loop ends, so a late shift by the
 * browser (keyboard pan undo, toolbar collapse) is visible on a phone
 * without a remote inspector. No-op otherwise. */
export const debugOn = () => typeof window !== 'undefined'
  && (window.__revealDebug === true || /[?&]reveal=debug(?:&|$)/.test(window.location.search));

export const debugLine = (text) => {
  if (!debugOn()) return;
  let box = document.getElementById('reveal-debug');
  if (!box) {
    box = document.createElement('pre');
    box.id = 'reveal-debug';
    box.style.cssText = 'position:fixed;left:0;right:0;top:0;z-index:99999;margin:0;padding:6px 8px;'
      + 'font:11px/1.35 monospace;background:rgba(0,0,0,.82);color:#9f9;pointer-events:none;'
      + 'white-space:pre-wrap;max-height:45vh;overflow:hidden';
    document.body.appendChild(box);
  }
  const lines = box.textContent.split('\n').filter(Boolean);
  lines.push(text);
  box.textContent = lines.slice(-24).join('\n');
  console.log('[reveal]', text); // eslint-disable-line no-console
};

const snapshot = (label, el, stickyEl) => {
  if (!debugOn()) return;
  const vv = window.visualViewport;
  const r = el ? el.getBoundingClientRect() : null;
  const s = stickyEl ? stickyEl.getBoundingClientRect() : null;
  const pos = stickyEl ? getComputedStyle(stickyEl).position : '-';
  debugLine(`${label} y=${Math.round(window.scrollY)} top=${r ? Math.round(r.top) : '-'} `
    + `stickyB=${s ? Math.round(s.bottom) : '-'}(${pos}) vv=${vv ? `${Math.round(vv.height)}@${Math.round(vv.offsetTop)}` : '-'} `
    + `ih=${window.innerHeight} root=${(document.querySelector('.pp') || {}).className || '-'}`);
};

/**
 * Scroll so `target` sits below `sticky` (or the viewport top when no sticky).
 *
 * @param {object} options
 * @param {Element|Function} options.target   element or resolver, re-resolved every frame
 * @param {Element|Function|null} options.sticky  sticky/fixed element (or resolver) whose
 *        bottom edge is the top of the visible area; null → viewport top
 * @param {number} options.gap                space between the sticky bottom and the target
 * @param {'top'|'nearest'} options.align     'top': target top = sticky bottom + gap;
 *        'nearest': only scroll when the target is outside the visible area
 *        (above → align top, below → align bottom, taller than the area → top wins)
 * @param {number} options.maxFrames          hard cap on correction frames
 * @param {number} options.tolerance          px within which the target counts as placed
 * @param {number} options.stableFrames       consecutive in-tolerance frames before finishing
 * @param {Function|null} options.onDone      called once when the loop ends (not on cancel)
 * @returns {Function} cancel
 */
export function revealBelowSticky(options = {}) {
  const {
    target,
    sticky = null,
    gap = REVEAL_GAP,
    align = 'top',
    maxFrames = 12,
    tolerance = 1,
    stableFrames = 2,
    onDone = null,
  } = options;
  if (typeof window === 'undefined') return () => {};

  let cancelled = false;
  let raf = 0;
  let frames = 0;
  let stable = 0;

  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const finish = (why) => {
    if (cancelled) return;
    cancelled = true;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    if (debugOn()) {
      debugLine(`done(${why}) frames=${frames} t=${Math.round((performance.now?.() ?? Date.now()) - t0)}ms`);
      [250, 750, 1500].forEach((ms) => setTimeout(() => snapshot(`+${ms}ms`, resolveEl(target), resolveEl(sticky)), ms));
    }
    onDone?.();
  };

  const step = () => {
    raf = 0;
    if (cancelled) return;
    const el = resolveEl(target);
    if (!el) { finish('no-target'); return; }

    const stickyEl = resolveEl(sticky);
    const stickyBottom = stickyEl ? Math.max(0, stickyEl.getBoundingClientRect().bottom) : 0;
    const rect = el.getBoundingClientRect();
    const topEdge = stickyBottom + gap;
    snapshot(`f${frames}`, el, stickyEl);
    // Layout-viewport coordinates throughout: the visible bottom is the visual
    // viewport's offset + height (iOS keeps the layout viewport in place and
    // offsets the visual one for the keyboard).
    const vv = window.visualViewport;
    const bottomEdge = (vv ? vv.offsetTop + vv.height : window.innerHeight) - gap;

    let delta = 0;
    if (align === 'top') delta = rect.top - topEdge;
    else if (rect.top < topEdge) delta = rect.top - topEdge;
    else if (rect.bottom > bottomEdge) delta = Math.min(rect.bottom - bottomEdge, rect.top - topEdge);

    if (Math.abs(delta) <= tolerance) {
      stable += 1;
      if (stable >= stableFrames) { finish('stable'); return; }
    } else {
      stable = 0;
      const before = window.scrollY;
      // ABSOLUTE target, never a relative scrollBy. iOS Safari scrolls
      // asynchronously: right after a scroll the page reports the new
      // position, a frame later the compositor can echo the previous one, and
      // it then applies every queued delta anyway — so chained scrollBy
      // corrections compounded (observed: 756 → 804 → read 756 → +48 again →
      // 852). `rect.top + scrollY` is the element's document position, the
      // same number whichever scroll the compositor has applied, so the
      // target below is identical from frame to frame and repeating it is
      // harmless.
      const targetY = Math.max(0, before + delta);
      window.scrollTo({ top: targetY, behavior: 'auto' });
      if (debugOn()) debugLine(`  scrollTo(${Math.round(targetY)}) → y=${Math.round(window.scrollY)}`);
      // At the scroll limit (or inside a pinned body) nothing moves — stop
      // rather than spin.
      if (Math.abs(window.scrollY - before) < 0.5) { finish('no-move'); return; }
    }

    frames += 1;
    if (frames >= maxFrames) { finish('max-frames'); return; }
    raf = requestAnimationFrame(step);
  };

  raf = requestAnimationFrame(step);
  return () => {
    cancelled = true;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
  };
}

/**
 * The focus-mode exit restore: wait for the keyboard/viewport to settle, then
 * converge. One cancel function for both phases.
 */
export function revealAfterKeyboard(revealOptions, settleOptions) {
  let cancelReveal = null;
  if (debugOn()) {
    const startedAt = performance.now?.() ?? Date.now();
    debugLine(`── reveal armed align=${revealOptions.align || 'top'}`);
    snapshot('arm', resolveEl(revealOptions.target), resolveEl(revealOptions.sticky));
    const cancelSettle = onViewportSettled(() => {
      debugLine(`settled after ${Math.round((performance.now?.() ?? Date.now()) - startedAt)}ms`);
      cancelReveal = revealBelowSticky(revealOptions);
    }, settleOptions);
    return () => { debugLine('cancelled'); cancelSettle(); cancelReveal?.(); };
  }
  const cancelSettle = onViewportSettled(() => {
    cancelReveal = revealBelowSticky(revealOptions);
  }, settleOptions);
  return () => {
    cancelSettle();
    cancelReveal?.();
  };
}

export default revealAfterKeyboard;
